import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type CodeRow = {
  id: string;
  code: string;
  app: string;
  label: string | null;
  notion_influencer_name: string | null;
  notion_handle: string | null;
  notion_page_id: string | null;
  used_count: number;
  max_uses: number | null;
  revoked_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
};

type NotionStatus = "Active" | "Used" | "Expired";

type StatusFilter = "all" | NotionStatus;

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

function deriveStatus(row: CodeRow): { status: NotionStatus; since: string | null } {
  const now = Date.now();
  if (row.revoked_at) return { status: "Expired", since: row.revoked_at };
  if (row.expires_at && new Date(row.expires_at).getTime() < now) {
    return { status: "Expired", since: row.expires_at };
  }
  if (row.used_count > 0) return { status: "Used", since: row.updated_at };
  return { status: "Active", since: row.created_at };
}

const statusVariant = (s: NotionStatus): { className: string; label: string } => {
  switch (s) {
    case "Active":
      return { className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30", label: "Active" };
    case "Used":
      return { className: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30", label: "Used" };
    case "Expired":
      return { className: "bg-muted text-muted-foreground border-border", label: "Expired" };
  }
};

export default function InfluencerCodeStatusWidget() {
  const [rows, setRows] = useState<CodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("influencer_access_codes")
      .select(
        "id, code, app, label, notion_influencer_name, notion_handle, notion_page_id, used_count, max_uses, revoked_at, expires_at, created_at, updated_at"
      )
      .order("updated_at", { ascending: false })
      .limit(200);
    if (error) {
      toast.error("Failed to load codes", { description: error.message });
      setRows([]);
    } else {
      setRows((data ?? []) as CodeRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const enriched = useMemo(
    () => rows.map((r) => ({ row: r, ...deriveStatus(r) })),
    [rows]
  );

  const counts = useMemo(() => {
    const c = { Active: 0, Used: 0, Expired: 0 } as Record<NotionStatus, number>;
    for (const e of enriched) c[e.status]++;
    return c;
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return enriched.filter(({ row, status }) => {
      if (filter !== "all" && status !== filter) return false;
      if (!q) return true;
      return (
        row.code.toLowerCase().includes(q) ||
        (row.label ?? "").toLowerCase().includes(q) ||
        (row.notion_influencer_name ?? "").toLowerCase().includes(q) ||
        (row.notion_handle ?? "").toLowerCase().includes(q) ||
        row.app.toLowerCase().includes(q)
      );
    });
  }, [enriched, filter, query]);

  return (
    <Card className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-medium">Influencer code status</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Live mirror of Notion status mapped from Supabase. Active → Used → Expired.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {(["Active", "Used", "Expired"] as NotionStatus[]).map((s) => {
          const v = statusVariant(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(filter === s ? "all" : s)}
              className={`text-left rounded-md border p-3 transition ${
                filter === s ? "ring-2 ring-ring" : ""
              } ${v.className}`}
            >
              <div className="text-xs uppercase tracking-wide opacity-80">{s}</div>
              <div className="text-2xl font-semibold mt-1">{counts[s]}</div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code, influencer, handle, app…"
            className="pl-8"
          />
        </div>
        <Select value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Used">Used</SelectItem>
            <SelectItem value="Expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border border-border overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
          <div className="col-span-3">Code / Influencer</div>
          <div className="col-span-2">App</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-2">Last activity</div>
          <div className="col-span-1 text-right">Uses</div>
        </div>
        {loading ? (
          <div className="px-3 py-6 text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-6 text-sm text-muted-foreground">No codes match.</div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map(({ row, status, since }) => {
              const v = statusVariant(status);
              const usage = row.max_uses ? `${row.used_count}/${row.max_uses}` : `${row.used_count}`;
              return (
                <li key={row.id} className="grid grid-cols-12 gap-2 px-3 py-3 text-sm items-center">
                  <div className="col-span-3 min-w-0">
                    <div className="font-mono text-xs truncate">{row.code}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {row.notion_influencer_name ?? row.label ?? "—"}
                      {row.notion_handle ? ` · ${row.notion_handle}` : ""}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Badge variant="outline" className="text-xs">{row.app}</Badge>
                  </div>
                  <div className="col-span-2">
                    <Badge variant="outline" className={`text-xs ${v.className}`}>
                      {v.label}
                    </Badge>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      since {fmt(since)}
                    </div>
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {fmt(row.created_at)}
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {fmt(row.updated_at)}
                    {row.expires_at && (
                      <div className="text-[10px]">expires {fmt(row.expires_at)}</div>
                    )}
                  </div>
                  <div className="col-span-1 text-right text-xs font-mono">{usage}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
